import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Coffee, Users, ShoppingBag, Palette } from 'lucide-react';

const Step1Earning = ({ earningMethods, onUpdate, onNext }) => {
  const [selectedMethods, setSelectedMethods] = useState(earningMethods);

  const earningOptions = [
    {
      id: 'donations',
      title: 'Receive donations or tips',
      description: 'Accept one-time contributions from supporters',
      icon: Coffee,
      color: '#e0c16f'
    },
    {
      id: 'memberships',
      title: 'Build monthly membership income',
      description: 'Create recurring subscription plans',
      icon: Users,
      color: '#FF6B6B'
    },
    {
      id: 'products',
      title: 'Sell products (physical or digital)',
      description: 'Offer items in your creator shop',
      icon: ShoppingBag,
      color: '#4ECDC4'
    },
    {
      id: 'commissions',
      title: 'Offer commissions or services',
      description: 'Provide custom work for supporters',
      icon: Palette,
      color: '#A8E6CF'
    }
  ];

  const handleToggleMethod = (methodId) => {
    const newMethods = selectedMethods.includes(methodId)
      ? selectedMethods.filter(id => id !== methodId)
      : [...selectedMethods, methodId];
    
    setSelectedMethods(newMethods);
    onUpdate({ earningMethods: newMethods });
  };

  const handleContinue = () => {
    if (selectedMethods.length > 0) {
      onNext();
    }
  };

  return (
    <StepContainer>
      <StepCard>
        <StepHeader>
          <StepTitle>How are you planning to earn?</StepTitle>
          <StepSubtitle>Select all the ways you want to monetize your content</StepSubtitle>
        </StepHeader>

        <OptionsContainer>
          {earningOptions.map((option, index) => (
            <motion.div
              key={option.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <OptionCard
                $selected={selectedMethods.includes(option.id)}
                onClick={() => handleToggleMethod(option.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <OptionIcon $color={option.color} style={{ background: option.color }}>
                  <option.icon size={24} />
                </OptionIcon>
                
                <OptionContent>
                  <OptionTitle>{option.title}</OptionTitle>
                  <OptionDescription>{option.description}</OptionDescription>
                </OptionContent>

                <Checkbox $selected={selectedMethods.includes(option.id)}>
                  {selectedMethods.includes(option.id) && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    >
                      ✓
                    </motion.div>
                  )}
                </Checkbox>
              </OptionCard>
            </motion.div>
          ))}
        </OptionsContainer>

        <ContinueButton
          onClick={handleContinue}
          disabled={selectedMethods.length === 0}
          $disabled={selectedMethods.length === 0}
          whileHover={{ scale: selectedMethods.length > 0 ? 1.05 : 1 }}
          whileTap={{ scale: selectedMethods.length > 0 ? 0.95 : 1 }}
        >
          Continue
        </ContinueButton>
      </StepCard>
    </StepContainer>
  );
};

const StepContainer = styled.div`
  width: 100%;
  max-width: 600px;
  display: flex;
  justify-content: center;
`;

const StepCard = styled.div`
  background: #111111;
  border: 1px solid #333333;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  width: 100%;
`;

const StepHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const StepTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  font-family: var(--font-heading);
  color: #ffffff;
  margin: 0 0 1rem 0;
`;

const StepSubtitle = styled.p`
  font-size: 1.1rem;
  font-family: var(--font-body);
  color: #cccccc;
  margin: 0;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 3rem;
`;

const OptionCard = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border: 2px solid ${props => props.$selected ? '#e0c16f' : '#333333'};
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.$selected ? 'rgba(224, 193, 111, 0.1)' : '#1a1a1a'};
  
  &:hover {
    border-color: #e0c16f;
    background: rgba(224, 193, 111, 0.1);
  }
`;

const OptionIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
  margin-right: 1rem;
  flex-shrink: 0;
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  font-family: var(--font-heading);
  color: #ffffff;
  margin: 0 0 0.5rem 0;
`;

const OptionDescription = styled.p`
  font-size: 0.95rem;
  font-family: var(--font-body);
  color: #cccccc;
  margin: 0;
`;

const Checkbox = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid ${props => props.$selected ? '#e0c16f' : '#555555'};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$selected ? '#e0c16f' : 'transparent'};
  color: #000000;
  font-weight: bold;
  font-size: 1rem;
  flex-shrink: 0;
`;

const ContinueButton = styled(motion.button)`
  width: 100%;
  padding: 1.2rem;
  background: ${props => props.$disabled ? '#333333' : 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)'};
  color: ${props => props.$disabled ? '#666666' : '#000000'};
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  font-family: var(--font-heading);
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$disabled ? '#333333' : 'linear-gradient(95.36deg, #bd851b 5.18%, #e0c16f 93.21%)'};
  }
`;

export default Step1Earning;